import React, { useState, useRef, useEffect } from 'react';
import { ConversationalAgentManager } from '../agents/ConversationalAgentManager.js';

const SmartChatAgent = ({ citizen }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agentManager, setAgentManager] = useState(null);
  const [conversationId] = useState(() => `chat_${Date.now()}`);
  const [isRecording, setIsRecording] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const messagesEndRef = useRef(null);
  const isInitialized = useRef(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);

  // تحديد الرسالة الأولية حسب نوع المواطن
  const getInitialMessage = () => {
    if (!citizen) return '';
    
    if (citizen.profile === 'domestic_violence_victim') {
      return 'السلام عليكم، والله محتاجة مساعدة ضرورية، زوجي يضربني وخايفة على نفسي وعلى اطفالي';
    } else if (citizen.profile === 'license_issue') {
      return 'السلام عليكم، راتبي ما نزل من شهرين وانا محتاج فلوس ضروري، عندي التزامات وما اعرف ايش اسوي';
    } else if (citizen.profile === 'password_reset') {
      return 'السلام عليكم والله عندي مشكلة من امس وانا احاول ادخل على حسابي في موقع وزارة العمل بس مايفتح معي كل مرة يقول كلمة المرور غلط';
    }
    return '';
  };

  // تهيئة Agent Manager وإرسال رسالة ترحيب
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    const apiKey = 'AIzaSyCmtyAVc5IC9Prh5E-lKVJlWOMZSjj6Gxs';
    const manager = new ConversationalAgentManager(apiKey);
    setAgentManager(manager);
    
    // رسالة ترحيب
    setTimeout(() => {
      addBotMessage('مرحباً! 👋 أنا Agent سبق الذكي.');
      setTimeout(() => {
        addBotMessage('كيف يمكنني مساعدتك اليوم؟');
      }, 800);
    }, 500);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const addBotMessage = (content, data = null) => {
    const botMessage = {
      id: Date.now() + Math.random(),
      type: 'bot',
      content: content,
      timestamp: new Date(),
      ...data
    };
    setMessages(prev => [...prev, botMessage]);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !agentManager) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputMessage,
      timestamp: new Date(),
      attachments: attachedFiles.length > 0 ? [...attachedFiles] : null
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setAttachedFiles([]);
    setIsLoading(true);

    try {
      const result = await agentManager.processMessage(userMessage.content, conversationId);
      console.log('📊 Result:', result);

      if (result.success && result.response) {
        addBotMessage(
          result.response.message,
          {
            scenario: result.response.scenario,
            scenarioDetails: result.response.scenarioDetails,
            trackingNumber: result.response.trackingNumber,
            estimatedTime: result.response.estimatedTime,
            actions: result.response.actions,
            nextSteps: result.response.nextSteps
          }
        );
      } else {
        addBotMessage(result.response.message || 'عذراً، حدث خطأ. يرجى المحاولة مرة أخرى.');
      }
    } catch (error) {
      console.error('❌ Error:', error);
      addBotMessage('عذراً، حدث خطأ تقني. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        setInputMessage('🎤 تم تسجيل رسالة صوتية');
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('خطأ في التسجيل:', error);
      alert('لم نتمكن من الوصول للميكروفون');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    setAttachedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const sendInitialMessage = () => {
    const message = getInitialMessage();
    if (message) {
      setInputMessage(message);
      setTimeout(() => {
        handleSendMessage();
      }, 100);
    }
  };

  return (
    <div>
      {/* معلومات المواطن */}
      {citizen && (
        <div style={{
          background: citizen.profile === 'domestic_violence_victim' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                      citizen.profile === 'license_issue' ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
                      'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          color: 'white',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '4px' }}>
                {citizen.profile === 'domestic_violence_victim' ? '🚨' :
                 citizen.profile === 'license_issue' ? '📄' : '🔑'} {citizen.name}
              </h3>
              <p style={{ fontSize: '14px', opacity: 0.9 }}>
                {citizen.issueType}
              </p>
            </div>
            <button
              onClick={sendInitialMessage}
              style={{
                background: 'white',
                color: citizen.profile === 'domestic_violence_victim' ? '#ef4444' :
                       citizen.profile === 'license_issue' ? '#f59e0b' : '#10b981',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              📤 إرسال الرسالة الأولية
            </button>
          </div>
        </div>
      )}

      {/* Chat Container */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '60vh'
      }}>
        {/* Messages Area */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          backgroundColor: '#f9fafb'
        }}>
          {messages.map((msg) => (
            <div key={msg.id} style={{
              display: 'flex',
              justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '16px'
            }}>
              <div style={{
                maxWidth: '70%',
                padding: '12px 16px',
                borderRadius: '16px',
                backgroundColor: msg.type === 'user' ? '#667eea' : 'white',
                color: msg.type === 'user' ? 'white' : '#1f2937',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                wordWrap: 'break-word'
              }}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>

                {/* عرض المرفقات */}
                {msg.attachments && msg.attachments.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                    📎 {msg.attachments.length} ملف مرفق
                  </div>
                )}

                {/* عرض تفاصيل السيناريو */}
                {msg.scenario && msg.scenarioDetails && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: msg.scenarioDetails.color + '15',
                    borderLeft: `4px solid ${msg.scenarioDetails.color}`,
                    borderRadius: '8px',
                    fontSize: '13px'
                  }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', color: msg.scenarioDetails.color }}>
                      {msg.scenarioDetails.icon} {msg.scenarioDetails.name}
                    </div>
                    <div style={{ marginBottom: '4px' }}>{msg.scenarioDetails.description}</div>
                    {msg.trackingNumber && (
                      <div style={{ marginTop: '8px', fontSize: '12px' }}>
                        📋 {msg.trackingNumber}
                      </div>
                    )}
                    {msg.estimatedTime && (
                      <div style={{ fontSize: '12px' }}>
                        ⏱️ {msg.estimatedTime}
                      </div>
                    )}
                    {msg.actions && msg.actions.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px' }}>
                        {msg.actions.map((action, idx) => (
                          <div key={idx}>✅ {action}</div>
                        ))}
                      </div>
                    )}
                    {msg.nextSteps && msg.nextSteps.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>📌 الخطوات التالية:</div>
                        {msg.nextSteps.map((step, idx) => (
                          <div key={idx}>• {step}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{
                  fontSize: '11px',
                  opacity: 0.6,
                  marginTop: '6px',
                  textAlign: msg.type === 'user' ? 'left' : 'right'
                }}>
                  {msg.timestamp.toLocaleTimeString('ar-SA', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div style={{
              display: 'flex',
              justifyContent: 'flex-start',
              marginBottom: '16px'
            }}>
              <div style={{
                padding: '12px 16px',
                borderRadius: '16px',
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '6px',
                  alignItems: 'center'
                }}>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <div className="typing-dot"></div>
                  <span style={{ marginRight: '8px', fontSize: '14px', color: '#666' }}>
                    Gemini AI يفكر...
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{
          padding: '16px',
          backgroundColor: 'white',
          borderTop: '2px solid #e5e7eb'
        }}>
          {attachedFiles.length > 0 && (
            <div style={{
              marginBottom: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {attachedFiles.map(file => (
                <div key={file.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  <span>
                    {file.type.startsWith('image/') ? '🖼️' : '📄'} {file.name}
                  </span>
                  <button
                    onClick={() => removeFile(file.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '16px',
                      padding: '0 4px'
                    }}
                  >
                    ❌
                  </button>
                </div>
              ))}
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            gap: '8px',
            alignItems: 'center'
          }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              style={{
                background: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px',
                cursor: 'pointer',
                fontSize: '20px',
                transition: 'all 0.2s'
              }}
              title="رفع ملف"
            >
              📎
            </button>

            <button
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isLoading}
              style={{
                background: isRecording ? '#ef4444' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px',
                cursor: 'pointer',
                fontSize: '20px',
                transition: 'all 0.2s',
                animation: isRecording ? 'pulse 1.5s infinite' : 'none'
              }}
              title={isRecording ? 'إيقاف التسجيل' : 'تسجيل صوتي'}
            >
              {isRecording ? '⏹️' : '🎤'}
            </button>

            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="اكتب رسالتك هنا... (اضغط Enter للإرسال)"
              disabled={isLoading || !agentManager}
              style={{
                flex: 1,
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '12px 16px',
                fontSize: '15px',
                outline: 'none',
                fontFamily: 'Arial, sans-serif',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#667eea'}
              onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputMessage.trim() || isLoading || !agentManager}
              style={{
                background: inputMessage.trim() && !isLoading && agentManager
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : '#d1d5db',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                cursor: inputMessage.trim() && !isLoading && agentManager ? 'pointer' : 'not-allowed',
                fontSize: '16px',
                fontWeight: 'bold',
                transition: 'all 0.2s',
                boxShadow: inputMessage.trim() && !isLoading && agentManager
                  ? '0 4px 12px rgba(102, 126, 234, 0.4)'
                  : 'none'
              }}
            >
              {isLoading ? '⏳' : '📤'} إرسال
            </button>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.7; }
          30% { transform: translateY(-10px); opacity: 1; }
        }
        
        .typing-dot {
          width: 8px;
          height: 8px;
          background: #667eea;
          border-radius: 50%;
          animation: typing 1.4s infinite;
        }
        
        .typing-dot:nth-child(2) {
          animation-delay: 0.2s;
        }
        
        .typing-dot:nth-child(3) {
          animation-delay: 0.4s;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default SmartChatAgent;
